import type { HazardKind, PropKind } from '@/components'
import { defineResource } from '@/core/ecs/World'
import type { Shape } from './shapes'

export interface PropSpec {
  kind: PropKind
  x: number
  z: number
  angle?: number
  /** Uniform visual scale; also scales the collider. */
  scale?: number
  seed?: number
}

export interface HazardSpec {
  kind: HazardKind
  x: number
  z: number
  radius?: number
  power?: number
  interval?: number
  pull?: number
  /** Phase offset so several burners do not fire in unison. */
  phase?: number
}

export interface SpawnPoint {
  x: number
  z: number
  angle: number
}

/**
 * Per-arena lighting. Every arena sets its own key light, fill, fog and
 * exposure, so the four maps read as four different places rather than one
 * place with the furniture moved around.
 */
export interface LightingProfile {
  /** Key light colour and strength. */
  sunColor: number
  sunIntensity: number
  sunPosition: [number, number, number]
  /** Hemisphere fill: sky above, bounce below. */
  skyColor: number
  bounceColor: number
  hemiIntensity: number
  ambientColor: number
  ambientIntensity: number
  fogColor: number
  fogDensity: number
  /** Backdrop behind the fog. */
  background: number
  /** Colour of the dome that catches the fog at the horizon. */
  domeColor: number
  /** Tone-mapping exposure. */
  exposure: number
  /** Emissive tone of the strip that marks the platform's edge. */
  edgeColor: number
}

/** Surface look of the platform itself. */
export interface SurfaceProfile {
  /** Base concrete tint, 0xRRGGBB. */
  tint: number
  /** Strength of the dark blotching, 0..1. */
  grime: number
  /** Strength of the rust bleed, 0..1. */
  rust: number
  /** Texture tiling across the platform. */
  repeat: number
  seed: number
}

export interface ArenaDef {
  id: string
  name: string
  /** Shown on the map-select card. */
  blurb: string
  shape: Shape
  centerX: number
  centerZ: number
  /** Half-extent of the footprint; drives camera framing and tile generation. */
  extentX: number
  extentZ: number
  spawns: [SpawnPoint, SpawnPoint]
  props: PropSpec[]
  hazards: HazardSpec[]
  lighting: LightingProfile
  surface: SurfaceProfile
  /** Extra camera pull-back multiplier for wide arenas. */
  cameraZoom: number
  /**
   * Size multiplier the arena was built with, 1 for the authored footprint.
   *
   * The collapse eats a fixed number of metres per second, so without this a
   * platform picked at 1.7× took 1.7× as long to close — measured, average
   * rounds went from 29 s to 61 s. Scaling the rate with the arena keeps the
   * *shape* of a round the same whatever size the players chose.
   */
  sizeScale?: number
}

export const COLLAPSE_START_SECONDS = 25
export const COLLAPSE_WARNING_SECONDS = 3
/** Effective radius lost per second once the collapse begins. */
export const COLLAPSE_RATE = 0.35
/**
 * How small the platform can get. Tuned against the headless harness: at 0.35
 * two fighters could still circle each other indefinitely on what was left and
 * rounds died to the 90 s timeout instead of to a knock-out. At 0.18 the
 * remaining ground is barely wider than the two of them, and a decision always
 * follows.
 */
export const COLLAPSE_MIN_SCALE = 0.18

/**
 * Runtime arena state. The footprint never changes; collapse is expressed as a
 * uniform scale about the arena centre, so `contains` just maps the query point
 * back into unscaled space before testing the SDF.
 */
export class ArenaState {
  scale = 1
  collapsing = false
  /** True during the few seconds of red-glow warning before the collapse. */
  warning = false

  constructor(public def: ArenaDef) {}

  get referenceRadius(): number {
    return Math.max(this.def.extentX, this.def.extentZ)
  }

  reset(def: ArenaDef = this.def): void {
    this.def = def
    this.scale = 1
    this.collapsing = false
    this.warning = false
  }

  /** Signed distance to the (possibly shrunken) edge, in world units. */
  distance(x: number, z: number): number {
    const s = this.scale
    const lx = this.def.centerX + (x - this.def.centerX) / s
    const lz = this.def.centerZ + (z - this.def.centerZ) / s
    return this.def.shape.distance(lx, lz) * s
  }

  contains(x: number, z: number): boolean {
    return this.distance(x, z) < 0
  }

  /** Advances the collapse. `roundTime` is seconds since the fight began. */
  update(roundTime: number, dt: number): void {
    this.warning =
      roundTime >= COLLAPSE_START_SECONDS - COLLAPSE_WARNING_SECONDS &&
      roundTime < COLLAPSE_START_SECONDS
    if (roundTime < COLLAPSE_START_SECONDS) return

    this.collapsing = true
    const rate = COLLAPSE_RATE * (this.def.sizeScale ?? 1)
    const scalePerSecond = rate / this.referenceRadius
    this.scale = Math.max(COLLAPSE_MIN_SCALE, this.scale - scalePerSecond * dt)
  }

  /**
   * Nearest point that is safely inside, used to place respawns and to nudge
   * props that the collapse would otherwise strand outside the platform.
   */
  clampInside(x: number, z: number, margin = 1): { x: number; z: number } {
    if (this.distance(x, z) < -margin) return { x, z }
    const cx = this.def.centerX
    const cz = this.def.centerZ
    let dx = x - cx
    let dz = z - cz
    const len = Math.hypot(dx, dz)
    if (len < 1e-4) return { x: cx, z: cz }
    dx /= len
    dz /= len
    // Walk inward until the SDF reports enough clearance.
    for (let t = len; t > 0; t -= 0.25) {
      const px = cx + dx * t
      const pz = cz + dz * t
      if (this.distance(px, pz) < -margin) return { x: px, z: pz }
    }
    return { x: cx, z: cz }
  }
}

export const ArenaResource = defineResource<ArenaState>('Arena')
